import { AnimatePresence, motion } from 'framer-motion';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './AlertProvider.css';

type AlertType = 'success' | 'fail' | 'warning' | 'info' | 'help';

interface Alert {
  id: string;
  type: AlertType;
  description: string;
}

interface AlertContextType {
  showAlert: (type: AlertType, description: string) => void;
}

const AlertContext = createContext<AlertContextType | null>(null);

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider = ({ children }: AlertProviderProps) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const capitalizeFirstLetter = (string: string): string => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const generateUniqueId = (): string => `${Date.now()}-${Math.random()}`;

  const lastAlertDescriptionRef = useRef<string | null>(null);

  const showAlert = useCallback((type: AlertType, description: string): void => {
    if (lastAlertDescriptionRef.current === description) return;
    lastAlertDescriptionRef.current = description;

    const id = generateUniqueId();
    setAlerts((prevAlerts) => [...prevAlerts, { id, type, description }]);

    setTimeout(() => {
      setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id));
      lastAlertDescriptionRef.current = null;
    }, 5000);
  }, []);

  const closeAlert = (id: string): void => {
    setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id));
  };

  const contextValue = useMemo(() => ({ showAlert }), [showAlert]);

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setAlerts([]);
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      <div className="alerts-container">
        <AnimatePresence>
          {alerts.map((alert) => (
            <motion.div
              key={alert.id}
              className={`alert alert-${alert.type}`}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3 }}
            >
              <button className="alert-close-btn" onClick={() => closeAlert(alert.id)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
              <div className="alert-content">
                <span>{capitalizeFirstLetter(alert.type)}</span>
                <p>{alert.description}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </AlertContext.Provider>
  );
};
